import { TaskMarkdownKatex } from '../../taskTypes/components/TaskMarkdownKatex';
import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useTaskTypeInstructions } from '../../hooks/useTaskTypeInstructions';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { paths } from '../../routes/paths';


export function TaskTypeInstructionsPage() {
  const { taskTypeId } = useParams<{ taskTypeId: string }>();
  const instructionsState = useTaskTypeInstructions(taskTypeId ?? null);
  if (!taskTypeId) {
    return <Navigate to={paths.queue()} replace />;
  }

  const descriptor = getTaskTypeDescriptor(taskTypeId);
  const knownTaskType = descriptor.id === taskTypeId;
  const instructions = instructionsState.instructions;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-gray-500">
                Task instructions
              </p>
              <h1 className="mt-1 text-2xl font-semibold text-gray-900">
                {knownTaskType ? descriptor.displayName : taskTypeId}
              </h1>
              <p className="mt-1 font-mono text-xs text-gray-500">{taskTypeId}</p>
            </div>
            <Link
              to={paths.queue()}
              className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back to queue
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {instructionsState.loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading instructions…
          </div>
        ) : instructionsState.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {instructionsState.error}
          </div>
        ) : !instructions ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-white p-8 text-sm text-gray-500">
            No instructions have been configured for this task type.
          </div>
        ) : instructions.kind !== 'markdown' ? (
          <div className="rounded-md border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-600">
              These instructions now live in Google Docs instead of inline markdown.
            </p>
            <a
              href={instructions.value}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-hover"
            >
              <ExternalLink className="h-4 w-4" />
              Open Google Doc
            </a>
          </div>
        ) : (
          <article className="rounded-xl border border-gray-200 bg-white px-6 py-8 shadow-sm sm:px-10">
            <TaskMarkdownKatex scale="prose" tone="transcript">
              {instructions.value}
            </TaskMarkdownKatex>
          </article>
        )}
      </main>
    </div>
  );
}
